import { useRef, useLayoutEffect, useState } from 'react';
import { useNode } from '@craftjs/core';
import clsx from 'clsx';
import { BackgroundImage } from './BackgroundImage';
import { BackgroundVideo } from '../BackgroundVideo/BackgroundVideo';
import type { PageProps } from '@builder4/shared/components/Page';
import type { ContainerProps } from '@builder4/shared/components/Container';
import type { BackgroundImageMode, BackgroundPosition } from '@builder4/shared/types/layout';
import type { VideoFitType, VideoProvider, VideoSourceType } from '@builder4/shared/types/video';
import { type ImageContinuousType, CONTINUOUS_DEFAULTS } from '@builder4/shared/types/animation';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

type BackgroundType = 'image' | 'video';

type NodeProps = PageProps | ContainerProps;

const NodeBackgroundSettings = () => {
  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const bgImage = getBPProp(props, 'bgImage');
  const bgImageId = getBPProp(props, 'bgImageId');
  const bgImageScale = getBPProp(props, 'bgImageScale');
  const bgImageOpacity = getBPProp(props, 'bgImageOpacity');
  const bgImageMode = getBPProp(props, 'bgImageMode');
  const bgImagePosition = getBPProp(props, 'bgImagePosition');
  const bgVideoSourceType = getBPProp(props, 'bgVideoSourceType');
  const bgVideoUrl = getBPProp(props, 'bgVideoUrl');
  const bgVideoProvider = getBPProp(props, 'bgVideoProvider');
  const bgVideoThumbnailUrl = getBPProp(props, 'bgVideoThumbnailUrl');
  const bgVideoEmbedCode = getBPProp(props, 'bgVideoEmbedCode');
  const bgVideoMode = getBPProp(props, 'bgVideoMode');
  const bgVideoOpacity = getBPProp(props, 'bgVideoOpacity');
  const bgContinuousAnimation = getBPProp(props, 'bgContinuousAnimation');
  const bgContinuousSpeed = getBPProp(props, 'bgContinuousSpeed');
  const bgContinuousIntensity = getBPProp(props, 'bgContinuousIntensity');

  // Determine initial background type based on current props
  const getInitialType = (): BackgroundType => {
    if (bgVideoUrl || bgVideoEmbedCode) return 'video';
    return 'image';
  };

  const [backgroundType, setBackgroundType] = useState<BackgroundType>(getInitialType);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const typeRefs = useRef<Record<BackgroundType, HTMLButtonElement | null>>({
    image: null,
    video: null,
  });

  // Update highlight position when type changes
  useLayoutEffect(() => {
    const activeEl = typeRefs.current[backgroundType];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [backgroundType]);

  const handleTypeChange = (newType: BackgroundType) => {
    setIsAnimated(true);
    setBackgroundType(newType);
  };

  // Image handlers
  const handleImageSelection = (bgImageUrl: string | null, bgImageId?: string) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgImage', bgImageUrl);
      setBPProp(props as PageProps, 'bgImageId', bgImageId ?? null);
      if (bgImageUrl) {
        setBPProp(props, 'bgVideoUrl', null);
        setBPProp(props, 'bgVideoEmbedCode', null);
        setBPProp(props, 'bgVideoThumbnailUrl', null);
        setBPProp(props, 'bgVideoProvider', undefined);
      }
    }, 500);
  };

  const handleScaleChange = (scale: number) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgImageScale', scale);
    }, 500);
  };

  const handleImageOpacityChange = (opacity: number) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgImageOpacity', opacity);
    }, 500);
  };

  const handleImageModeChange = (mode: BackgroundImageMode) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgImageMode', mode);
    }, 500);
  };

  const handleImagePositionChange = (position: BackgroundPosition) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgImagePosition', position);
    }, 500);
  };

  const handleContinuousAnimationChange = (type: ImageContinuousType) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgContinuousAnimation', type);
      if (type !== 'none') {
        const defaults = CONTINUOUS_DEFAULTS[type];
        setBPProp(props, 'bgContinuousSpeed', defaults.speed);
        setBPProp(props, 'bgContinuousIntensity', defaults.intensity);
      } else {
        setBPProp(props, 'bgContinuousSpeed', undefined);
        setBPProp(props, 'bgContinuousIntensity', undefined);
      }
    }, 500);
  };

  const handleContinuousSpeedChange = (speed: number) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgContinuousSpeed', speed);
    }, 500);
  };

  const handleContinuousIntensityChange = (intensity: number) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgContinuousIntensity', intensity);
    }, 500);
  };

  // Video handlers
  const handleVideoSourceTypeChange = (sourceType: VideoSourceType) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoSourceType', sourceType);
    }, 500);
  };

  const handleVideoUrlChange = (
    url: string | null,
    provider?: VideoProvider,
    thumbnailUrl?: string | null,
  ) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoUrl', url);
      setBPProp(props, 'bgVideoProvider', provider);
      setBPProp(props, 'bgVideoThumbnailUrl', thumbnailUrl);
      if (url) {
        setBPProp(props, 'bgImage', null);
      }
    }, 500);
  };

  const handleVideoEmbedCodeChange = (embedCode: string | null) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoEmbedCode', embedCode);
      if (embedCode) {
        setBPProp(props, 'bgImage', null);
      }
    }, 500);
  };

  const handleVideoThumbnailChange = (thumbnailUrl: string | null) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoThumbnailUrl', thumbnailUrl);
    }, 500);
  };

  const handleVideoModeChange = (mode: VideoFitType) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoMode', mode);
    }, 500);
  };

  const handleVideoOpacityChange = (opacity: number) => {
    setProp((props: NodeProps) => {
      setBPProp(props, 'bgVideoOpacity', opacity);
    }, 500);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="pb-1 text-xs font-semibold text-neutral-900">Background</p>

      {/* Type Toggle */}
      <div className="flex w-full max-w-[216px] items-center justify-between gap-4">
        <p className="text-button-sm font-medium text-neutral-500">Type</p>
        <div className="relative flex h-8 w-[160px] items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
          <div
            className={clsx(
              'absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0]',
              isAnimated && 'transition-all duration-300',
            )}
            style={{
              width: `${highlightStyle.width}px`,
              transform: `translateX(${highlightStyle.left}px)`,
            }}
          >
            <p className="text-button-sm font-semibold capitalize">{backgroundType}</p>
          </div>
          {(['image', 'video'] as const).map((type) => (
            <button
              key={type}
              ref={(el) => {
                typeRefs.current[type] = el;
              }}
              onClick={() => handleTypeChange(type)}
              className="text-button-sm relative z-10 flex-1 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
              data-testid={`background-type-${type}`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Image Controls */}
      {backgroundType === 'image' && (
        <BackgroundImage
          selectedImage={bgImage}
          selectedImageId={bgImageId}
          scale={bgImageScale}
          opacity={bgImageOpacity}
          position={bgImagePosition}
          mode={bgImageMode}
          className="w-full max-w-[216px]"
          onImageSelected={handleImageSelection}
          onScaleChange={handleScaleChange}
          onOpacityChange={handleImageOpacityChange}
          onModeChange={handleImageModeChange}
          onPositionChange={handleImagePositionChange}
          continuousAnimation={bgContinuousAnimation}
          continuousSpeed={bgContinuousSpeed}
          continuousIntensity={bgContinuousIntensity}
          onContinuousAnimationChange={handleContinuousAnimationChange}
          onContinuousSpeedChange={handleContinuousSpeedChange}
          onContinuousIntensityChange={handleContinuousIntensityChange}
        />
      )}

      {/* Video Controls */}
      {backgroundType === 'video' && (
        <BackgroundVideo
          sourceType={bgVideoSourceType}
          onSourceTypeChange={handleVideoSourceTypeChange}
          videoUrl={bgVideoUrl}
          provider={bgVideoProvider}
          thumbnailUrl={bgVideoThumbnailUrl}
          onVideoUrlChange={handleVideoUrlChange}
          embedCode={bgVideoEmbedCode}
          onEmbedCodeChange={handleVideoEmbedCodeChange}
          onThumbnailChange={handleVideoThumbnailChange}
          mode={bgVideoMode}
          onModeChange={handleVideoModeChange}
          opacity={bgVideoOpacity}
          onOpacityChange={handleVideoOpacityChange}
          className="w-full max-w-[216px]"
        />
      )}
    </div>
  );
};

export default NodeBackgroundSettings;
